interface ThemeColors {
  // Background colors
  primary: string
  secondary: string
  surface: string
  overlay: string
  
  // Text colors
  textPrimary: string
  textSecondary: string
  textTertiary: string
  
  // Border colors
  border: string
  borderLight: string
  
  // Interactive colors
  accent: string
  accentHover: string
  accentActive: string
  
  // Status colors
  success: string
  error: string
  warning: string
  
  // Special effects
  shadow: string
  backdropFilter: string
}

export class ThemeManager {
  private currentTheme: 'light' | 'dark' = 'dark'
  private listeners: Set<(theme: 'light' | 'dark') => void> = new Set()
  private mediaQuery: MediaQueryList | null = null
  private mutationObserver: MutationObserver | null = null

  constructor() {
    this.detectInitialTheme()
    this.setupListeners()
  }

  private detectInitialTheme(): void {
    // Priority order for theme detection:
    // 1. Website's explicit theme classes/attributes
    // 2. System preference
    // 3. Default to dark

    const websiteTheme = this.detectWebsiteTheme()
    if (websiteTheme) {
      this.currentTheme = websiteTheme
      return
    }

    // Check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) {
      this.currentTheme = 'light'
    } else {
      this.currentTheme = 'dark'
    }
  }

  private detectWebsiteTheme(): 'light' | 'dark' | null {
    // Common theme detection patterns
    const html = document.documentElement
    const body = document.body

    // Check for common dark mode classes/attributes
    const darkPatterns = [
      'dark',
      'dark-mode',
      'dark-theme',
      'theme-dark',
      'data-theme="dark"',
      'data-color-mode="dark"',
      'data-bs-theme="dark"' // Bootstrap
    ]

    const lightPatterns = [
      'light',
      'light-mode',
      'light-theme',
      'theme-light',
      'data-theme="light"',
      'data-color-mode="light"',
      'data-bs-theme="light"'
    ]

    // Check HTML element
    for (const pattern of darkPatterns) {
      if (html.classList.contains(pattern) || 
          html.getAttribute('data-theme') === 'dark' ||
          html.getAttribute('data-color-mode') === 'dark' ||
          html.getAttribute('data-bs-theme') === 'dark') {
        return 'dark'
      }
    }

    for (const pattern of lightPatterns) {
      if (html.classList.contains(pattern) || 
          html.getAttribute('data-theme') === 'light' ||
          html.getAttribute('data-color-mode') === 'light' ||
          html.getAttribute('data-bs-theme') === 'light') {
        return 'light'
      }
    }

    // Check body element
    for (const pattern of darkPatterns) {
      if (body.classList.contains(pattern)) {
        return 'dark'
      }
    }

    for (const pattern of lightPatterns) {
      if (body.classList.contains(pattern)) {
        return 'light'
      }
    }

    // Check computed styles for background color hints
    const bodyStyles = window.getComputedStyle(body)
    const htmlStyles = window.getComputedStyle(html)
    
    const bodyBg = bodyStyles.backgroundColor
    const htmlBg = htmlStyles.backgroundColor

    // If background is very dark, assume dark theme
    if (this.isColorDark(bodyBg) || this.isColorDark(htmlBg)) {
      return 'dark'
    }

    // If background is very light, assume light theme
    if (this.isColorLight(bodyBg) || this.isColorLight(htmlBg)) {
      return 'light'
    }

    return null
  }

  private isColorDark(color: string): boolean {
    if (!color || color === 'transparent' || color === 'rgba(0, 0, 0, 0)') return false
    
    const rgb = this.parseColor(color)
    if (!rgb) return false
    
    // Calculate relative luminance
    const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255
    return luminance < 0.5
  }

  private isColorLight(color: string): boolean {
    if (!color || color === 'transparent' || color === 'rgba(0, 0, 0, 0)') return false
    
    const rgb = this.parseColor(color)
    if (!rgb) return false
    
    // Calculate relative luminance
    const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255
    return luminance > 0.8
  }

  private parseColor(color: string): { r: number; g: number; b: number } | null {
    // Handle rgb/rgba
    const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/)
    if (rgbMatch) {
      return {
        r: parseInt(rgbMatch[1]),
        g: parseInt(rgbMatch[2]),
        b: parseInt(rgbMatch[3])
      }
    }

    // Handle hex colors
    const hexMatch = color.match(/^#([a-f\d]{6})$/i)
    if (hexMatch) {
      const hex = hexMatch[1]
      return {
        r: parseInt(hex.substr(0, 2), 16),
        g: parseInt(hex.substr(2, 2), 16),
        b: parseInt(hex.substr(4, 2), 16)
      }
    }

    return null
  }

  private setupListeners(): void {
    // Listen for system theme changes
    if (window.matchMedia) {
      this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      this.mediaQuery.addListener(() => {
        this.handleThemeChange()
      })
    }

    // Listen for website theme changes
    this.mutationObserver = new MutationObserver((mutations) => {
      let shouldUpdate = false

      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') {
          const target = mutation.target as Element
          const attrName = mutation.attributeName

          // Check if theme-related attributes changed
          if (attrName === 'class' || 
              attrName === 'data-theme' ||
              attrName === 'data-color-mode' ||
              attrName === 'data-bs-theme') {
            shouldUpdate = true
          }
        }
      })

      if (shouldUpdate) {
        this.handleThemeChange()
      }
    })

    // Observe both html and body for theme changes
    this.mutationObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme', 'data-color-mode', 'data-bs-theme']
    })

    this.mutationObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class', 'data-theme', 'data-color-mode', 'data-bs-theme']
    })
  }

  private handleThemeChange(): void {
    const oldTheme = this.currentTheme
    this.detectInitialTheme()
    
    if (oldTheme !== this.currentTheme) {
      this.notifyListeners()
    }
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener(this.currentTheme)
      } catch (error) {
        console.error('FeedLet: Theme listener error:', error)
      }
    })
  }

  public getCurrentTheme(): 'light' | 'dark' {
    return this.currentTheme
  }

  public onThemeChange(callback: (theme: 'light' | 'dark') => void): () => void {
    this.listeners.add(callback)
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback)
    }
  }

  public getThemeColors(accentColor: string = '#4f46e5'): ThemeColors {
    if (this.currentTheme === 'light') {
      return {
        // Background colors
        primary: '#ffffff',
        secondary: '#f8fafc',
        surface: '#ffffff',
        overlay: 'rgba(0, 0, 0, 0.5)',
        
        // Text colors
        textPrimary: '#1f2937',
        textSecondary: '#6b7280',
        textTertiary: '#9ca3af',
        
        // Border colors
        border: '#e5e7eb',
        borderLight: '#f3f4f6',
        
        // Interactive colors
        accent: accentColor,
        accentHover: this.adjustColor(accentColor, -20),
        accentActive: this.adjustColor(accentColor, -40),
        
        // Status colors
        success: '#10b981',
        error: '#ef4444',
        warning: '#f59e0b',
        
        // Special effects
        shadow: 'rgba(0, 0, 0, 0.1)',
        backdropFilter: 'blur(8px)'
      }
    } else {
      return {
        // Background colors
        primary: '#1a1a1a',
        secondary: '#2a2a2a',
        surface: '#1a1a1a',
        overlay: 'rgba(0, 0, 0, 0.7)',
        
        // Text colors
        textPrimary: '#ffffff',
        textSecondary: '#d1d5db',
        textTertiary: '#9ca3af',
        
        // Border colors
        border: '#374151',
        borderLight: '#4b5563',
        
        // Interactive colors
        accent: accentColor,
        accentHover: this.adjustColor(accentColor, 20),
        accentActive: this.adjustColor(accentColor, 40),
        
        // Status colors
        success: '#22c55e',
        error: '#ef4444',
        warning: '#f59e0b',
        
        // Special effects
        shadow: 'rgba(0, 0, 0, 0.3)',
        backdropFilter: 'blur(8px)'
      }
    }
  }

  private adjustColor(color: string, amount: number): string {
    const usePound = color[0] === '#'
    const col = usePound ? color.slice(1) : color
    const num = parseInt(col, 16)
    
    let r = (num >> 16) + amount
    let g = (num >> 8 & 0x00FF) + amount
    let b = (num & 0x0000FF) + amount
    
    r = r > 255 ? 255 : r < 0 ? 0 : r
    g = g > 255 ? 255 : g < 0 ? 0 : g
    b = b > 255 ? 255 : b < 0 ? 0 : b
    
    return (usePound ? '#' : '') + (r << 16 | g << 8 | b).toString(16).padStart(6, '0')
  }

  public applyCSSVariables(accentColor: string = '#4f46e5'): void {
    const colors = this.getThemeColors(accentColor)
    const root = document.documentElement

    // Apply CSS custom properties
    root.style.setProperty('--feedlet-bg-primary', colors.primary)
    root.style.setProperty('--feedlet-bg-secondary', colors.secondary)
    root.style.setProperty('--feedlet-bg-surface', colors.surface)
    root.style.setProperty('--feedlet-bg-overlay', colors.overlay)
    
    root.style.setProperty('--feedlet-text-primary', colors.textPrimary)
    root.style.setProperty('--feedlet-text-secondary', colors.textSecondary)
    root.style.setProperty('--feedlet-text-tertiary', colors.textTertiary)
    
    root.style.setProperty('--feedlet-border', colors.border)
    root.style.setProperty('--feedlet-border-light', colors.borderLight)
    
    root.style.setProperty('--feedlet-accent', colors.accent)
    root.style.setProperty('--feedlet-accent-hover', colors.accentHover)
    root.style.setProperty('--feedlet-accent-active', colors.accentActive)
    
    root.style.setProperty('--feedlet-success', colors.success)
    root.style.setProperty('--feedlet-error', colors.error)
    root.style.setProperty('--feedlet-warning', colors.warning)
    
    root.style.setProperty('--feedlet-shadow', colors.shadow)
    root.style.setProperty('--feedlet-backdrop-filter', colors.backdropFilter)
  }

  public destroy(): void {
    if (this.mediaQuery) {
      this.mediaQuery.removeListener(this.handleThemeChange)
    }

    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
    }

    this.listeners.clear()
  }
} 